import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import Select from "components/inputs/Select/Select";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useMemo, useState } from "react";
import { useCopyMedicalRecordsFromMetadocMutation } from "services/future-medical-api";
import type { Metadoc } from "services/metadoc-api";

import { getCopyMedicalRecordsServiceType } from "../../future-medical-utils";
import type { MedicalHistoryTableProps } from "../medicalRecordUtils";
import { getErrorMessage } from "../medicalRecordUtils";

import type { ApiError } from "types/ApiTypes";
import type { OptionType } from "components/inputs/Select/Select";

type CopyMedicalRecordsFromMetadocModalProps = {
  isOpen: boolean;
  onClose: () => void;
  service: MedicalHistoryTableProps["service"];
  metadocs: Metadoc[];
  hasExistingRecords: boolean;
};

const CopyMedicalRecordsFromMetadocModal = ({
  isOpen,
  onClose,
  service,
  metadocs,
  hasExistingRecords,
}: CopyMedicalRecordsFromMetadocModalProps) => {
  const serviceType = getCopyMedicalRecordsServiceType(service);
  const [selectedMetadoc, setSelectedMetadoc] = useState<OptionType | null>(
    null,
  );
  const [step, setStep] = useState<"select" | "confirm">("select");
  const [copyMedicalRecordsFromMetadoc, { isLoading: isSubmitting }] =
    useCopyMedicalRecordsFromMetadocMutation();

  const metadocOptions = useMemo(
    () =>
      metadocs.map((metadoc) => ({
        label: metadoc.name,
        value: metadoc.id,
      })),
    [metadocs],
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedMetadoc(null);
      setStep("select");
      return;
    }

    if (metadocs.length === 1) {
      setSelectedMetadoc({
        label: metadocs[0].name,
        value: metadocs[0].id,
      });
    }
  }, [isOpen, metadocs]);

  const handleSubmit = async () => {
    if (!selectedMetadoc?.value || !serviceType) {
      return;
    }

    try {
      const response = await copyMedicalRecordsFromMetadoc({
        serviceId: service.id,
        serviceType,
        metadocId: Number(selectedMetadoc.value),
      }).unwrap();

      KaboodleToaster.success(
        response.detail || "Medical records imported from AI results.",
      );
      onClose();
    } catch (error) {
      console.error("Failed to import medical records from metadoc:", error);
      KaboodleToaster.error(
        getErrorMessage(
          error as ApiError,
          "Failed to import medical records from AI results.",
        ),
      );
    }
  };

  if (step === "confirm") {
    return (
      <KaboodleModal
        isOpen={isOpen}
        onClose={onClose}
        title="Import From Metadoc"
        footer={
          <div className="flex jc-c">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="delete"
            >
              {isSubmitting ? "Importing..." : "Yes, Import and Replace"}
            </Button>
            <Button onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        }
      >
        <Card heading="Are you sure?">
          <p>
            Importing from <strong>{selectedMetadoc?.label}</strong> will remove
            all current items from the Medical Record Review table, including
            any manually entered records.
          </p>
          <p>This action cannot be undone.</p>
        </Card>
      </KaboodleModal>
    );
  }

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      title="Import From Metadoc"
      footer={
        <div className="flex jc-c">
          <Button
            onClick={() =>
              hasExistingRecords ? setStep("confirm") : handleSubmit()
            }
            disabled={!selectedMetadoc || !serviceType}
          >
            Import Medical Records
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Select Metadoc">
        <Select
          label="Metadoc"
          name="metadoc"
          placeholder="Select a metadoc..."
          options={metadocOptions}
          value={selectedMetadoc}
          onChange={(option) => setSelectedMetadoc(option as OptionType | null)}
          required
          isClearable={metadocs.length > 1}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CopyMedicalRecordsFromMetadocModal;
